import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Switch,
  message,
  Popconfirm,
  Tag,
  Typography,
  Card,
  InputNumber,
  Select,
  Row,
  Col,
  Statistic,
  Tooltip,
  Alert,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  DeploymentUnitOutlined,
  BarChartOutlined,
  CheckCircleOutlined,
  StopOutlined,
} from '@ant-design/icons';
import { modelApi } from '../../services/api';
import { 
  ModelConfig, 
  CreateModelRequest, 
  UpdateModelRequest,
  ModelStats 
} from '../../types';
import type { ColumnsType } from 'antd/es/table';
import { usePermissions, usePermissionCheck, PermissionGuard } from '../../context/PermissionContext';

const { Title, Text } = Typography;
const { Option } = Select;

const ModelManagement: React.FC = () => {
  const [models, setModels] = useState<ModelConfig[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [current, setCurrent] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [stats, setStats] = useState<ModelStats | null>(null);
  
  // 过滤器状态
  const [providerFilter, setProviderFilter] = useState<string>('');
  const [modelNameFilter, setModelNameFilter] = useState<string>('');
  const [enabledFilter, setEnabledFilter] = useState<boolean | undefined>(undefined);
  
  // 模态框状态
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [statsModalVisible, setStatsModalVisible] = useState(false);
  const [selectedModel, setSelectedModel] = useState<ModelConfig | null>(null);

  // 表单实例
  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();

  // 权限检查
  const { loading: permissionLoading } = usePermissions();
  const { hasPermission } = usePermissionCheck();

  // 检查权限的便捷方法
  const canViewModels = () => hasPermission('model.view');
  const canCreateModels = () => hasPermission('model.create');
  const canEditModels = () => hasPermission('model.edit');
  const canDeleteModels = () => hasPermission('model.delete');

  useEffect(() => {
    if (!permissionLoading && canViewModels()) {
      fetchModels();
      fetchStats();
    }
  }, [permissionLoading, current, pageSize, providerFilter, enabledFilter, modelNameFilter]);

  const fetchModels = async () => {
    try {
      setLoading(true);
      const response = await modelApi.getModels(current, pageSize, providerFilter, enabledFilter, modelNameFilter);
      setModels(response.data.data.models);
      setTotal(response.data.data.total);
    } catch (error) {
      message.error('获取大模型列表失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await modelApi.getModelStats();
      setStats(response.data.data);
    } catch (error) {
      console.error('获取统计信息失败:', error);
    }
  };

  const handleCreateModel = async (values: CreateModelRequest) => {
    try {
      await modelApi.createModel(values);
      message.success('创建大模型成功');
      setCreateModalVisible(false);
      createForm.resetFields();
      fetchModels();
      fetchStats();
    } catch (error) {
      message.error('创建大模型失败');
    }
  };

  const handleEditModel = (model: ModelConfig) => {
    setSelectedModel(model);
    editForm.setFieldsValue({
      model_name: model.model_name,
      display_name: model.display_name,
      description: model.description,
      provider: model.provider,
      price_per_token: model.price_per_token,
      input_price: model.input_price,
      output_price: model.output_price,
      max_tokens: model.max_tokens,
      is_enabled: model.is_enabled,
      sort: model.sort,
      tags: model.tags,
    });
    setEditModalVisible(true);
  };

  const handleUpdateModel = async (values: UpdateModelRequest) => {
    if (!selectedModel) return;
    
    try {
      await modelApi.updateModel(selectedModel.id, values);
      message.success('更新大模型成功');
      setEditModalVisible(false);
      editForm.resetFields();
      setSelectedModel(null);
      fetchModels();
      fetchStats();
    } catch (error) {
      message.error('更新大模型失败');
    }
  };

  const handleDeleteModel = async (id: number) => {
    try {
      await modelApi.deleteModel(id);
      message.success('删除大模型成功');
      fetchModels();
      fetchStats();
    } catch (error) {
      message.error('删除大模型失败');
    }
  };

  const handleToggleStatus = async (id: number) => {
    try {
      await modelApi.toggleModelStatus(id);
      message.success('状态切换成功');
      fetchModels();
      fetchStats();
    } catch (error) {
      message.error('状态切换失败');
    }
  };

  const handleBatchStatusChange = async (enabled: boolean) => {
    if (selectedRowKeys.length === 0) {
      message.warning('请先选择要操作的模型');
      return;
    }

    try {
      await modelApi.batchUpdateStatus({
        ids: selectedRowKeys as number[],
        enabled: enabled,
      });
      message.success(`批量${enabled ? '启用' : '禁用'}成功`);
      setSelectedRowKeys([]);
      fetchModels();
      fetchStats();
    } catch (error) {
      message.error(`批量${enabled ? '启用' : '禁用'}失败`);
    }
  };

  const handleBatchDelete = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请先选择要删除的模型');
      return;
    }

    try {
      await modelApi.batchDelete({
        ids: selectedRowKeys as number[],
      });
      message.success('批量删除成功');
      setSelectedRowKeys([]);
      fetchModels();
      fetchStats();
    } catch (error) {
      message.error('批量删除失败');
    }
  };

  const handleFilterChange = () => {
    setCurrent(1);
    fetchModels();
  };

  const resetFilters = () => {
    setProviderFilter('');
    setModelNameFilter('');
    setEnabledFilter(undefined);
    setCurrent(1);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys: React.Key[]) => {
      setSelectedRowKeys(newSelectedRowKeys);
    },
  };

  const columns: ColumnsType<ModelConfig> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '模型名称',
      dataIndex: 'model_name',
      key: 'model_name',
      render: (text: string) => (
        <Text code style={{ fontSize: '12px' }}>{text}</Text>
      ),
    },
    {
      title: '显示名称',
      dataIndex: 'display_name',
      key: 'display_name',
      render: (text: string) => (
        <Text strong>{text}</Text>
      ),
    },
    {
      title: '提供商',
      dataIndex: 'provider',
      key: 'provider',
      render: (provider: string) => {
        const colors: Record<string, string> = {
          openai: 'green',
          deepseek: 'blue',
          anthropic: 'purple',
          google: 'orange',
          default: 'default',
        };
        return <Tag color={colors[provider] || colors.default}>{provider}</Tag>;
      },
      filters: [
        { text: 'OpenAI', value: 'openai' },
        { text: 'DeepSeek', value: 'deepseek' },
        { text: 'Anthropic', value: 'anthropic' },
        { text: 'Google', value: 'google' },
      ],
      onFilter: (value, record) => record.provider === value,
    },
    {
      title: '输入价格',
      dataIndex: 'input_price',
      key: 'input_price',
      render: (price: number) => (
        <Text type="success">¥{price.toFixed(6)}/千token</Text>
      ),
      sorter: (a, b) => a.input_price - b.input_price,
    },
    {
      title: '输出价格',
      dataIndex: 'output_price',
      key: 'output_price',
      render: (price: number) => (
        <Text type="warning">¥{price.toFixed(6)}/千token</Text>
      ),
      sorter: (a, b) => a.output_price - b.output_price,
    },
    {
      title: '调用次数',
      dataIndex: 'call_count',
      key: 'call_count',
      render: (count: number) => (
        <Text>{count.toLocaleString()}</Text>
      ),
      sorter: (a, b) => a.call_count - b.call_count,
    },
    {
      title: '总费用',
      dataIndex: 'total_cost',
      key: 'total_cost',
      render: (cost: number) => (
        <Text type="success">¥{cost.toFixed(2)}</Text>
      ),
      sorter: (a, b) => a.total_cost - b.total_cost,
    },
    {
      title: '状态',
      dataIndex: 'is_enabled',
      key: 'is_enabled',
      render: (enabled: boolean) => (
        <Tag 
          color={enabled ? 'success' : 'default'} 
          icon={enabled ? <CheckCircleOutlined /> : <StopOutlined />}
        >
          {enabled ? '启用' : '禁用'}
        </Tag>
      ),
      filters: [
        { text: '启用', value: true },
        { text: '禁用', value: false },
      ],
      onFilter: (value, record) => record.is_enabled === value,
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => {
        if (!date) {
          return <Text type="secondary">-</Text>;
        }
        return <Text type="secondary">{new Date(date).toLocaleString('zh-CN')}</Text>;
      },
      sorter: (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
    },
    {
      title: '操作',
      key: 'actions',
      fixed: 'right',
      width: 200,
      render: (_, record) => (
        <Space size="small">
          <PermissionGuard permission="model.btn_edit">
            <Tooltip title="编辑">
              <Button 
                type="text" 
                icon={<EditOutlined />} 
                onClick={() => handleEditModel(record)}
                disabled={!canEditModels()}
              />
            </Tooltip>
          </PermissionGuard>
          
          <PermissionGuard permission="model.btn_toggle">
            <Tooltip title={record.is_enabled ? '禁用' : '启用'}>
              <Button 
                type="text" 
                icon={record.is_enabled ? <StopOutlined /> : <CheckCircleOutlined />}
                onClick={() => handleToggleStatus(record.id)}
                disabled={!canEditModels()}
              />
            </Tooltip>
          </PermissionGuard>

          <PermissionGuard permission="model.btn_delete">
            <Popconfirm
              title="确定要删除这个大模型吗？"
              description="删除后将无法恢复，请谨慎操作。"
              onConfirm={() => handleDeleteModel(record.id)}
              disabled={!canDeleteModels()}
            >
              <Tooltip title="删除">
                <Button 
                  type="text" 
                  danger 
                  icon={<DeleteOutlined />}
                  disabled={!canDeleteModels()}
                />
              </Tooltip>
            </Popconfirm>
          </PermissionGuard>
        </Space>
      ),
    },
  ];

  if (permissionLoading) {
    return <div>检查权限中...</div>;
  }

  if (!canViewModels()) {
    return <div>您没有权限访问此页面</div>;
  }

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>
        <DeploymentUnitOutlined /> 大模型管理
      </Title>

      {/* 统计卡片 */}
      {stats && (
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={6}>
            <Card>
              <Statistic
                title="总模型数"
                value={stats.summary.total_models}
                prefix={<DeploymentUnitOutlined />}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="启用模型数"
                value={stats.summary.enabled_models}
                prefix={<CheckCircleOutlined />}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="总调用次数"
                value={stats.summary.total_calls}
                valueStyle={{ color: '#722ed1' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="总收入"
                value={stats.summary.total_revenue}
                precision={2}
                prefix="¥"
                valueStyle={{ color: '#f5222d' }}
              />
            </Card>
          </Col>
        </Row>
      )}

      <Card>
        {/* 工具栏 */}
        <div style={{ marginBottom: 16 }}>
          <Row gutter={16} align="middle">
            <Col flex="auto">
              <Space>
                <PermissionGuard permission="model.btn_create">
                  <Button 
                    type="primary" 
                    icon={<PlusOutlined />}
                    onClick={() => setCreateModalVisible(true)}
                    disabled={!canCreateModels()}
                  >
                    新增模型
                  </Button>
                </PermissionGuard>

                <Button 
                  icon={<ReloadOutlined />} 
                  onClick={fetchModels}
                >
                  刷新
                </Button>

                <Button 
                  icon={<BarChartOutlined />} 
                  onClick={() => setStatsModalVisible(true)}
                >
                  查看统计
                </Button>
              </Space>
            </Col>
            
            <Col>
              <Space>
                <Input.Search
                  placeholder="搜索模型名称"
                  allowClear
                  style={{ width: 200 }}
                  value={modelNameFilter}
                  onChange={(e) => setModelNameFilter(e.target.value)}
                  onSearch={handleFilterChange}
                />
                <Input.Search
                  placeholder="按提供商筛选"
                  allowClear
                  style={{ width: 160 }}
                  value={providerFilter}
                  onChange={(e) => setProviderFilter(e.target.value)}
                  onSearch={handleFilterChange}
                />
                
                <Select
                  placeholder="状态筛选"
                  allowClear
                  style={{ width: 120 }}
                  value={enabledFilter}
                  onChange={(value) => {
                    setEnabledFilter(value);
                    handleFilterChange();
                  }}
                >
                  <Option value={true}>启用</Option>
                  <Option value={false}>禁用</Option>
                </Select>

                <Button onClick={resetFilters}>重置</Button>
              </Space>
            </Col>
          </Row>

          {/* 批量操作 */}
          {selectedRowKeys.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <Alert
                message={`已选择 ${selectedRowKeys.length} 个模型`}
                type="info"
                showIcon
                action={
                  <Space>
                    <PermissionGuard permission="model.btn_batch">
                      <Button 
                        size="small"
                        onClick={() => handleBatchStatusChange(true)}
                        disabled={!canEditModels()}
                      >
                        批量启用
                      </Button>
                      <Button 
                        size="small"
                        onClick={() => handleBatchStatusChange(false)}
                        disabled={!canEditModels()}
                      >
                        批量禁用
                      </Button>
                      <Popconfirm
                        title="确定要批量删除选中的模型吗？"
                        onConfirm={handleBatchDelete}
                        disabled={!canDeleteModels()}
                      >
                        <Button 
                          size="small" 
                          danger
                          disabled={!canDeleteModels()}
                        >
                          批量删除
                        </Button>
                      </Popconfirm>
                    </PermissionGuard>
                  </Space>
                }
                closable
                onClose={() => setSelectedRowKeys([])}
              />
            </div>
          )}
        </div>

        {/* 表格 */}
        <Table
          columns={columns}
          dataSource={models}
          rowKey="id"
          loading={loading}
          rowSelection={rowSelection}
          pagination={{
            current,
            pageSize,
            total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => 
              `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
            onChange: (page, size) => {
              setCurrent(page);
              setPageSize(size || 10);
            },
          }}
          scroll={{ x: 1200 }}
        />
      </Card>

      {/* 创建模型模态框 */}
      <Modal
        title="新增大模型"
        open={createModalVisible}
        onCancel={() => {
          setCreateModalVisible(false);
          createForm.resetFields();
        }}
        footer={null}
        width={800}
      >
        <Form
          form={createForm}
          layout="vertical"
          onFinish={handleCreateModel}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="model_name"
                label="模型名称"
                rules={[{ required: true, message: '请输入模型名称' }]}
              >
                <Input placeholder="如: gpt-4-turbo" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="display_name"
                label="显示名称"
                rules={[{ required: true, message: '请输入显示名称' }]}
              >
                <Input placeholder="如: GPT-4 Turbo" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="provider"
                label="提供商"
                rules={[{ required: true, message: '请选择提供商' }]}
              >
                <Select 
                  placeholder="选择提供商"
                  dropdownStyle={{ minWidth: 200 }}
                >
                  <Option value="openai">OpenAI</Option>
                  <Option value="deepseek">DeepSeek</Option>
                  <Option value="anthropic">Anthropic</Option>
                  <Option value="google">Google</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="max_tokens"
                label="最大Token数"
                rules={[{ required: true, message: '请输入最大Token数' }]}
              >
                <InputNumber 
                  min={1} 
                  max={1000000} 
                  style={{ width: '100%' }}
                  placeholder="如: 4096"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="price_per_token"
                label="每Token价格(元)"
                rules={[{ required: true, message: '请输入每Token价格' }]}
              >
                <InputNumber 
                  min={0} 
                  step={0.000001}
                  precision={6}
                  style={{ width: '100%' }}
                  placeholder="0.000001"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="input_price"
                label="输入价格(元/千token)"
                rules={[{ required: true, message: '请输入输入价格' }]}
              >
                <InputNumber 
                  min={0} 
                  step={0.001}
                  precision={6}
                  style={{ width: '100%' }}
                  placeholder="0.001"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="output_price"
                label="输出价格(元/千token)"
                rules={[{ required: true, message: '请输入输出价格' }]}
              >
                <InputNumber 
                  min={0} 
                  step={0.001}
                  precision={6}
                  style={{ width: '100%' }}
                  placeholder="0.002"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="sort"
                label="排序权重"
                initialValue={0}
              >
                <InputNumber 
                  style={{ width: '100%' }}
                  placeholder="数字越大排序越靠前"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="is_enabled"
                label="是否启用"
                initialValue={true}
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="description"
            label="描述"
          >
            <Input.TextArea 
              rows={3} 
              placeholder="模型描述信息"
            />
          </Form.Item>

          <Form.Item
            name="tags"
            label="标签"
          >
            <Input placeholder='JSON格式，如: ["chat", "completion"]' />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => {
                setCreateModalVisible(false);
                createForm.resetFields();
              }}>
                取消
              </Button>
              <Button type="primary" htmlType="submit">
                创建
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 编辑模型模态框 */}
      <Modal
        title="编辑大模型"
        open={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false);
          editForm.resetFields();
          setSelectedModel(null);
        }}
        footer={null}
        width={800}
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={handleUpdateModel}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="model_name"
                label="模型名称"
                rules={[{ required: true, message: '请输入模型名称' }]}
              >
                <Input placeholder="如: gpt-4-turbo" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="display_name"
                label="显示名称"
                rules={[{ required: true, message: '请输入显示名称' }]}
              >
                <Input placeholder="如: GPT-4 Turbo" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="provider"
                label="提供商"
                rules={[{ required: true, message: '请选择提供商' }]}
              >
                <Select 
                  placeholder="选择提供商"
                  dropdownStyle={{ minWidth: 200 }}
                >
                  <Option value="openai">OpenAI</Option>
                  <Option value="deepseek">DeepSeek</Option>
                  <Option value="anthropic">Anthropic</Option>
                  <Option value="google">Google</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="max_tokens"
                label="最大Token数"
                rules={[{ required: true, message: '请输入最大Token数' }]}
              >
                <InputNumber 
                  min={1} 
                  max={1000000} 
                  style={{ width: '100%' }}
                  placeholder="如: 4096"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="price_per_token"
                label="每Token价格(元)"
                rules={[{ required: true, message: '请输入每Token价格' }]}
              >
                <InputNumber 
                  min={0} 
                  step={0.000001}
                  precision={6}
                  style={{ width: '100%' }}
                  placeholder="0.000001"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="input_price"
                label="输入价格(元/千token)"
                rules={[{ required: true, message: '请输入输入价格' }]}
              >
                <InputNumber 
                  min={0} 
                  step={0.001}
                  precision={6}
                  style={{ width: '100%' }}
                  placeholder="0.001"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="output_price"
                label="输出价格(元/千token)"
                rules={[{ required: true, message: '请输入输出价格' }]}
              >
                <InputNumber 
                  min={0} 
                  step={0.001}
                  precision={6}
                  style={{ width: '100%' }}
                  placeholder="0.002"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="sort"
                label="排序权重"
              >
                <InputNumber 
                  style={{ width: '100%' }}
                  placeholder="数字越大排序越靠前"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="is_enabled"
                label="是否启用"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="description"
            label="描述"
          >
            <Input.TextArea 
              rows={3} 
              placeholder="模型描述信息"
            />
          </Form.Item>

          <Form.Item
            name="tags"
            label="标签"
          >
            <Input placeholder='JSON格式，如: ["chat", "completion"]' />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => {
                setEditModalVisible(false);
                editForm.resetFields();
                setSelectedModel(null);
              }}>
                取消
              </Button>
              <Button type="primary" htmlType="submit">
                更新
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 统计模态框 */}
      <Modal
        title="大模型统计信息"
        open={statsModalVisible}
        onCancel={() => setStatsModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setStatsModalVisible(false)}>
            关闭
          </Button>
        ]}
        width={1000}
      >
        {stats && (
          <div>
            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col span={6}>
                <Statistic
                  title="总模型数"
                  value={stats.summary.total_models}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="启用模型数"
                  value={stats.summary.enabled_models}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="总调用次数"
                  value={stats.summary.total_calls}
                  valueStyle={{ color: '#722ed1' }}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="总收入"
                  value={stats.summary.total_revenue}
                  precision={2}
                  prefix="¥"
                  valueStyle={{ color: '#f5222d' }}
                />
              </Col>
            </Row>

            <Table
              columns={[
                {
                  title: '模型名称',
                  dataIndex: 'model_name',
                  key: 'model_name',
                },
                {
                  title: '显示名称',
                  dataIndex: 'display_name',
                  key: 'display_name',
                },
                {
                  title: '提供商',
                  dataIndex: 'provider',
                  key: 'provider',
                  render: (provider: string) => <Tag>{provider}</Tag>,
                },
                {
                  title: '调用次数',
                  dataIndex: 'call_count',
                  key: 'call_count',
                  sorter: (a, b) => a.call_count - b.call_count,
                },
                {
                  title: '总费用',
                  dataIndex: 'total_cost',
                  key: 'total_cost',
                  render: (cost: number) => `¥${cost.toFixed(2)}`,
                  sorter: (a, b) => a.total_cost - b.total_cost,
                },
                {
                  title: '状态',
                  dataIndex: 'is_enabled',
                  key: 'is_enabled',
                  render: (enabled: boolean) => (
                    <Tag color={enabled ? 'success' : 'default'}>
                      {enabled ? '启用' : '禁用'}
                    </Tag>
                  ),
                },
              ]}
              dataSource={stats.models}
              rowKey="model_name"
              pagination={false}
              size="small"
            />
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ModelManagement; 